/**
 * External dependencies
 */
import { useState, useCallback, useMemo } from 'react';
import useRouter from '@naruto/naruto-hooks/src/useRouter';

/**
 * Internal dependencies
 */
import useLang from '@naruto/bgdapp/src/hooks/useLang';
import useReduxApi from '@naruto/bgdapp/src/hooks/useReduxApi';
import useMappedState from '@naruto/bgdapp/src/hooks/useMappedState';
import useValidRoute from '@naruto/bgdapp/src/hooks/useValidRoute';
import { TabItem, RouteItem, Option } from './types';

interface UseData {
  /* Data : General */
  menuOpen: boolean;
  langOptions: [Option<string>, Option<string>];
  sizeOptions: Option<number>[];
  currentLang: string;
  fontScale: number;
  mainRoutes: RouteItem[];
  anchorEl: HTMLButtonElement | null;
  /* Functions: General */
  toggleMenu: () => void;
  gotoHome: () => void;
  onSelected: (item: TabItem) => void;
  handleScaleChange: (scale: number) => void;
  handleLangChange: (lang: string) => void;
  handleChangeRoute: (route: string) => void;
  handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleClose: () => void;
}

function useData(): UseData {
  /* Global & Local State */
  const { i18ns } = useLang();
  const { push } = useRouter();
  const storeGlobal = useMappedState(state => state.global);
  const reduxGlobal = useReduxApi('global');
  const validRoutes = useValidRoute([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  /* Data */
  const langOptions: [Option<string>, Option<string>] = i18ns._getCurrentI18n().langAvailableList.map(avalible => ({
    label: i18ns.__(`frontend.global.property.language_${avalible.name}`),
    value: avalible.value
  })) as [Option<string>, Option<string>];
  const sizeOptions: Option<number>[] = useMemo(
    () => [
      { label: '100%', value: 1 },
      { label: '125%', value: 1.25 },
      { label: '150%', value: 1.5 }
    ],
    []
  );
  const currentLang = useMemo(() => storeGlobal.globalLang, [storeGlobal.globalLang]);

  /* Functions : General */
  const handleScaleChange = useCallback(
    (scale: number): void => {
      reduxGlobal('postGlobalFontScale', [scale]);
    },
    [storeGlobal.globalFontScale]
  );
  const handleLangChange = useCallback(
    (lang: string): void => {
      i18ns.setCurrentLanguage(lang);
      reduxGlobal('postGlobalLang', [lang]);
    },
    [storeGlobal.globalLang]
  );
  const toggleMenu = useCallback((): void => {
    setMenuOpen(!menuOpen);
  }, [menuOpen]);
  const gotoHome = useCallback((): void => {
    push('/');
  }, []);
  const handleChangeRoute = useCallback((route: string): void => {
    push(route);
    setMenuOpen(false);
  }, []);
  const onSelected = useCallback(
    (item: TabItem): void => {
      push(item.value);
      setMenuOpen(false);
    },
    [menuOpen]
  );
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [anchorEl]
  );
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [anchorEl]);

  /* Main */
  return {
    /* Data : General */
    menuOpen,
    langOptions,
    sizeOptions,
    currentLang,
    fontScale: storeGlobal.globalFontScale,
    mainRoutes: validRoutes.routes,
    anchorEl,
    /* Functions : General */
    toggleMenu,
    gotoHome,
    onSelected,
    handleScaleChange,
    handleLangChange,
    handleChangeRoute,
    handleClick,
    handleClose
  };
}

export default useData;
